import { KeyNames } from "@regrello/core-utils";
import { useRef } from "react";
import { useMount } from "react-use";

export namespace useDataGridV2FirstKeystroke {
  export type Args<T> = {
    /** The `eventKey` from the RegrelloDataGrid event. */
    eventKey: string | null;

    /** Callback to invoke to change the value of the cell. */
    onValueChange: (value: T) => void;

    /** Callback to invoke to convert the `eventKey` into the appropriate type for this input. */
    parseEventKey: (eventKey: string) => T;

    /** The current value to display in the cell. */
    value: T;
  };

  export type Return = {
    /** A ref that should be passed to the input element. */
    inputRef: React.RefObject<HTMLInputElement>;
  };
}

/**
 * Hook that replaces the current value if the user pressed a printable key to enter edit mode.
 * This runs logic only on mount, so no need to memoize anything.
 */
export function useDataGridV2FirstKeystroke<T>({
  eventKey,
  onValueChange,
  parseEventKey,
  value,
}: useDataGridV2FirstKeystroke.Args<T>): useDataGridV2FirstKeystroke.Return {
  const inputRef = useRef<HTMLInputElement>(null);

  // Replace the current value if the user pressed a printable key to enter edit mode.
  useMount(() => {
    if (eventKey === KeyNames.ENTER) {
      // Select all text.
      inputRef.current?.setSelectionRange(0, value != null ? String(value).length : 0);
    } else if (eventKey != null) {
      onValueChange(parseEventKey(eventKey));
    }
  });

  return { inputRef };
}
